export interface SegmentOption<V extends string> {
  value: V;
  label: string;
  disabled?: boolean;
  /** id of an element explaining why the segment is disabled. */
  describedBy?: string;
  /** Marks the segment (e.g. "translation missing"): a dot plus part of the accessible name. */
  hint?: string;
}

interface Props<V extends string> {
  options: readonly SegmentOption<V>[];
  value: V;
  onChange: (value: V) => void;
  "aria-label": string;
  /** Floating over the map: render on glass. */
  glass?: boolean;
  className?: string;
}

/** Design `SegmentedControl`: 2-4 mutually exclusive views; `button[aria-pressed]` children. */
export function SegmentedControl<V extends string>({
  options,
  value,
  onChange,
  glass,
  className,
  ...rest
}: Props<V>) {
  return (
    <div
      role="group"
      aria-label={rest["aria-label"]}
      className={["ds-segmented", glass && "ds-glass", className].filter(Boolean).join(" ")}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          aria-label={o.hint ? `${o.label}, ${o.hint}` : undefined}
          disabled={o.disabled}
          aria-describedby={o.disabled ? o.describedBy : undefined}
          onClick={() => onChange(o.value)}
        >
          {o.label}
          {o.hint ? <span className="seg-dot" aria-hidden="true" /> : null}
        </button>
      ))}
    </div>
  );
}
